import { useEffect } from 'react';
import { Outlet } from 'react-router-dom';
import { Users } from 'lucide-react';
import { useAdvisorFirm } from './useAdvisorFirm';
import { AdvisorFirmChooser } from './AdvisorFirmChooser';
import { clearActingFirmId, useActingFirmId } from './advisorActingFirm';
import { useClientFrame } from './useClientFrame';
import { useWorkspace } from '../workspace/WorkspaceProvider';
import { EmptyState } from '../components/EmptyState';
import { btns } from '../components/buttons';

/**
 * Route guard for the whole `/advisor` subtree (docs/25 §2, S7) — mirrors
 * `RequireAdmin` (People → Access, Settings → Workspace): resolves `useAdvisorFirm()`
 * ONCE for every page underneath, so `AdvisorClients`, `AdvisorIntakes`, and
 * `ClientProfile` don't each reproduce the same loading/denied pair of checks.
 * Same as `RequireAdmin`, this renders NOTHING while resolving rather than a
 * page-specific skeleton — the query is workspace-less and typically already
 * warm from the nav (`Layout.tsx` calls the same `useAdvisorFirm()` to decide
 * whether to show the nav section at all), so the null frame is brief, and a
 * placeholder here would have to guess which child page it is standing in for.
 *
 * ⚠️ **UX GATING ONLY, NEVER AUTHORIZATION** — see `useAdvisorFirm`'s own header,
 * which this component does nothing to change. Rendering the denied message
 * means "hide the page", never "access was checked and refused"; the actual
 * boundary is still appcore's per-request membership check on whatever a child
 * page fetches next. A child that somehow reached this far and called the
 * façade with a workspace id it isn't a member of still gets a plain 403 from
 * appcore, exactly as if this guard did not exist.
 *
 * **Why a route wrapper and not a hook each page calls itself.** "Not an
 * advisor" is a TERMINAL state, distinct from "still loading" — and because a
 * child page's own reads are naturally `enabled: !!advisorFor`, a per-page hook
 * leaves every future `/advisor` page free to rediscover that distinction (or
 * miss it) on its own, the way this file's first draft had to spell out in a
 * comment on `ClientProfile.tsx`. Deciding it once, here, makes it structurally
 * impossible for a new page to get wrong: a page under this guard never
 * observes the ambiguous "query disabled forever" state, only "not mounted
 * yet" (this component's own null/EmptyState) or "ready".
 *
 * Children re-call `useAdvisorFirm()` themselves for the `advisorFor` value
 * (same query key, already resolved and cached by the time `<Outlet/>` below
 * ever renders, so it costs nothing) and assert it non-null — same convention
 * as `useAccessToken()!` elsewhere in this app — rather than this component
 * threading it down via `Outlet` context, which would be a new pattern with no
 * other precedent in this router.
 *
 * ⚠️ **THREE terminal states now, not two** (steward-app#983). `advisorFor` null used to
 * mean "not an advisor" and nothing else; it can now also mean "an advisor whose firm this
 * request has not named", and the two are told apart by `firmChoice`. Rendering the denied
 * message over a `firmChoice` would lock an operator staff at two registered firms out of
 * the portal for BOTH of them, which is option 1 from that issue's body and is exactly what
 * Matt rejected on 2026-08-31. The order of the branches below is therefore load-bearing:
 * the choice is offered BEFORE the refusal, never after it.
 *
 * Deciding it here rather than in each page is the same argument this component already
 * makes about "not an advisor": a page under this guard never observes the ambiguous state
 * at all, so `AdvisorClients`, `AdvisorOverview`, `AdvisorIntakes` and `ClientProfile` keep
 * asserting `advisorFor!` exactly as they do today.
 *
 * ⚠️ **Also the self-heal for a stale client workspace under ANY route in this subtree**
 * (steward-app#1401, moved here from `AdvisorClients.tsx` by this branch's own independent
 * review). `AdvisorClients.tsx`'s `openClient` pushes a history entry rather than replacing
 * it specifically so browser back can leave a client, but nothing in this app listens for a
 * history pop and re-invokes `switchWorkspace` — a pop only changes the URL. Landing back
 * (or a bookmark, or a typed URL) on ANY page under this guard — not just Clients — with a
 * client workspace still active would otherwise render `Layout.tsx`'s band and sidebar exit
 * item over a FIRM page (Overview, Clients, Prospects, Your firm), the exact hybrid state
 * #1401 exists to remove; fixing this only on `AdvisorClients` left every sibling route
 * exposed. `useClientFrame()` is called unconditionally, ahead of the early returns below,
 * because hooks cannot follow a conditional return — its own `inClientFrame` reads `false`
 * while `advisorFor` is still resolving, so the effect does nothing during that window.
 *
 * A generic history listener was considered and rejected: a family member's OWN
 * in-workspace navigation (Dashboard to Portfolio and back) is indistinguishable from the
 * stale-back case at the history-API level, and "fixing" a pop within a still-current
 * client workspace would incorrectly evict a family member mid-session. Keying on "this
 * guard's own subtree, with a client still active" avoids that: nothing under `/advisor`
 * is a family member's own workspace tour, so `inClientFrame` being true here is ALWAYS the
 * stale case, never a legitimate one to preserve. `exitToClients()` is the same durable
 * correction every other exit already performs, and its `navigate(..., { replace: true })`
 * (the default) is itself a correction rather than a fifth equivalent switch: it also drops
 * whatever forward-history entry the stale page had, which is intended, not a side effect
 * to work around — see `docs/04-navigation-ia.md` §4.5 for why "four exits" undersold it.
 *
 * ⚠️ **`!switchInFlight` guards the line above it, and removing that guard reintroduces
 * steward-app#1409** (a P1 regression this same self-heal caused, fixed 2026-09-11). The
 * advisor Clients page's own "Open" (`AdvisorClients.tsx`'s `openClient`) is itself a
 * `switchWorkspace(clientId, '/', ...)` call, and `WorkspaceProvider`'s `activeWorkspaceId`
 * and react-router's `useLocation()` do NOT always land in the same commit: `setSelectedId`
 * is an ordinary synchronous `setState`, while react-router 7 wraps its own location-state
 * update in `React.startTransition` (verified against `RequireAdvisorFirm.test.tsx`'s
 * sibling `openClient.regression.test.tsx`, which logs both on every render). React gives
 * the synchronous update priority, so `openClient` commits at least one real, painted
 * render where `inClientFrame` is already true — this guard is still mounted, still showing
 * `/advisor/clients` — while the route hasn't caught up to the `navigate('/')` that same
 * click asked for. Without `!switchInFlight` this effect fired on THAT render and switched
 * straight back to the firm before the intended navigation ever became visible, so the
 * click read as inert. `switchInFlight` (`WorkspaceProvider`'s own docblock has the full
 * mechanism) is the settled signal that tells this transitional commit apart from a
 * genuinely stale route (browser back, a bookmark, a typed URL): only the former has a
 * switch whose destination the URL hasn't reached yet. It does NOT narrow which routes this
 * self-heal covers — a stale route reached with no switch in flight (`switchInFlight` is
 * false whenever `WorkspaceProvider` hasn't been asked to navigate anywhere) still self-heals
 * exactly as before, which is what the `RequireAdvisorFirm — self-heals a stale client
 * workspace under ANY child route` describe block below pins.
 */
export function RequireAdvisorFirm() {
  const { advisorFor, firmChoice, isLoading } = useAdvisorFirm();
  const acting = useActingFirmId();
  const { inClientFrame, exitToClients } = useClientFrame();
  const { switchInFlight } = useWorkspace();

  useEffect(() => {
    if (inClientFrame && !switchInFlight) exitToClients();
  }, [inClientFrame, switchInFlight, exitToClients]);

  if (isLoading) return null;
  // Before the refusal, deliberately — see the ⚠️ above.
  if (firmChoice) return <AdvisorFirmChooser firms={firmChoice} />;
  if (!advisorFor) {
    return <EmptyState icon={Users} message="This area is for advisory-firm team members." />;
  }
  return (
    <>
      {/* Shown only to someone who was ASKED, which is what `acting` records: a tab that
          never saw a chooser has nothing stored, so the single-firm advisor every real
          production firm has today sees no switcher at all (steward-app#983 D4 — building
          one into the nav for them is the scope creep that decision names).

          "Change firm" simply forgets the selection. It deliberately does not re-render a
          remembered list: the offered set is the server's answer, so clearing sends no
          `firmId`, the façade offers the list again, and the chooser above comes back. */}
      {acting && (
        <div className="flex flex-wrap items-center justify-end gap-2 px-1 pb-2">
          <span className="text-slate text-[12px] font-sans">
            Working as {advisorFor.name}.
          </span>
          <button type="button" onClick={clearActingFirmId} className={btns.btnSm}>
            Change firm
          </button>
        </div>
      )}
      <Outlet />
    </>
  );
}
